import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Unlink2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { StorageDetailResponse } from "@/types/api";

type GatewayRouteSummary = NonNullable<
  StorageDetailResponse["gatewayRouteSummary"]
>;
type GatewayRoute = NonNullable<GatewayRouteSummary["routes"]>[number];

const ROUTE_AVAILABILITY_CLASSES: Record<
  GatewayRoute["routeAvailabilityState"],
  string
> = {
  available: "border-emerald-200 bg-emerald-50 text-emerald-800",
  checking: "border-amber-200 bg-amber-50 text-amber-800",
  attention: "border-red-200 bg-red-50 text-red-800",
};

function formatDateTime(value: string | null | undefined, emptyLabel: string) {
  return value ? new Date(value).toLocaleString("ja-JP") : emptyLabel;
}

export function GatewayRouteSummaryPanel(props: {
  summary: GatewayRouteSummary;
  title: string;
  maxRouteCount: number;
  className?: string;
  onDeleteRoute?: (route: GatewayRoute) => void;
  deleteDisabled?: boolean;
  limitReachedMessage?: string;
}) {
  const { t } = useTranslation("storages");
  const routes = props.summary.routes ?? [];
  const [expandedRouteKey, setExpandedRouteKey] = useState<string | null>(null);
  const usageLabel = t("detail.gatewayRouteUsageValue", {
    used: props.summary.activeRouteCount,
    max: props.maxRouteCount,
  });

  return (
    <div className={props.className ?? "rounded-2xl border border-slate-200 bg-slate-50/70 p-4"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="font-medium text-slate-950">{props.title}</p>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">
            {t("detail.gatewayRouteUsageLabel")}
          </p>
          <p className="text-sm font-semibold tabular-nums text-slate-950">
            {usageLabel}
          </p>
        </div>
      </div>
      {props.limitReachedMessage ? (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {props.limitReachedMessage}
        </p>
      ) : null}
      {routes.length > 0 ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm font-medium text-slate-950">
            {t("detail.gatewayRouteList")}
          </p>
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {routes.map((route) => {
              const routeKey = `${route.gatewayInstanceId}:${route.routeId}`;
              const expanded = expandedRouteKey === routeKey;
              return (
                <li
                  key={routeKey}
                  className="transition-colors hover:bg-slate-50"
                >
                  <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-950">
                          {route.gatewayDisplayName ??
                            t("detail.gatewayRouteUnnamedGateway")}
                        </p>
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${ROUTE_AVAILABILITY_CLASSES[route.routeAvailabilityState]}`}
                        >
                          {t(
                            `detail.gatewayRouteAvailabilityStates.${route.routeAvailabilityState}`,
                          )}
                        </span>
                      </div>
                      {route.attentionReasonCode ? (
                        <p className="text-sm text-red-700">
                          {t(
                            `detail.gatewayRouteAttentionReasons.${route.attentionReasonCode}`,
                          )}
                        </p>
                      ) : null}
                      <p className="text-xs text-slate-600">
                        {t("detail.gatewayRouteLastHealthCheckedAt")}:{" "}
                        {formatDateTime(
                          route.lastHealthCheckedAt,
                          t("detail.gatewayRouteLastHealthCheckedAtEmpty"),
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-expanded={expanded}
                        onClick={() =>
                          setExpandedRouteKey(expanded ? null : routeKey)
                        }
                      >
                        <ChevronDown
                          className={`mr-2 h-4 w-4 transition-transform ${
                            expanded ? "rotate-180" : ""
                          }`}
                        />
                        {t("detail.gatewayRouteDetailsCta")}
                      </Button>
                      {props.onDeleteRoute ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => props.onDeleteRoute?.(route)}
                          disabled={props.deleteDisabled}
                        >
                          <Unlink2 className="mr-2 h-4 w-4" />
                          {t("detail.gatewayRouteDeleteCta")}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  {expanded ? (
                    <dl className="grid gap-3 border-t border-slate-200 bg-slate-50/70 p-3 text-xs text-slate-700 sm:grid-cols-2">
                      <div>
                        <dt className="text-slate-500">
                          {t("detail.gatewayRouteLastAttachedAt")}
                        </dt>
                        <dd className="mt-1 font-medium text-slate-950">
                          {formatDateTime(
                            route.attachedAt,
                            t("detail.gatewayRouteLastAttachedAtEmpty"),
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">
                          {t("detail.gatewayRouteLastHealthCheckedAt")}
                        </dt>
                        <dd className="mt-1 font-medium text-slate-950">
                          {formatDateTime(
                            route.lastHealthCheckedAt,
                            t("detail.gatewayRouteLastHealthCheckedAtEmpty"),
                          )}
                        </dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-slate-500">
                          {t("detail.operabilityWarnings")}
                        </dt>
                        <dd className="mt-1 font-medium text-slate-950">
                          {route.attentionReasonCode
                            ? t(
                                `detail.gatewayRouteAttentionReasons.${route.attentionReasonCode}`,
                              )
                            : t("detail.gatewayRouteNoAttention")}
                        </dd>
                      </div>
                    </dl>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
